import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronRight, Loader2, User } from 'lucide-react';
import type { UseFormHandleSubmit, UseFormRegister, FieldErrors } from 'react-hook-form';
import { EmailValidationIndicator } from '@shared/ui/EmailValidationIndicator';
import { LocaleLink } from '@lib/i18n/exports';
import type { MultiMethodLoginFormData } from './types';

interface UiConfig {
  headerTitle: string;
  headerSubtitle: string;
  headerSpacing: string;
  inputHeight: string;
  primaryBtnHeight: string;
  sectionGap: string;
}

interface NameStepProps {
  ui: UiConfig;
  email: string;
  error: string | null;
  isNameValid: boolean;
  isConsentValid: boolean;
  isSendingOtp: boolean;
  emailValidationStatus: string;
  emailInvalidReason: string | null;
  onChangeEmail: () => void;
  register: UseFormRegister<MultiMethodLoginFormData>;
  errors: FieldErrors<MultiMethodLoginFormData>;
  handleSubmit: UseFormHandleSubmit<MultiMethodLoginFormData>;
  onSubmitName: (data: MultiMethodLoginFormData) => void;
}

export const NameStep: React.FC<NameStepProps> = ({
  ui,
  email,
  error,
  isNameValid,
  isConsentValid,
  isSendingOtp,
  emailValidationStatus,
  emailInvalidReason,
  onChangeEmail,
  register,
  errors,
  handleSubmit,
  onSubmitName
}) => {
  const { t } = useTranslation('auth');
  return (
    <>
      <div className="sr-only">Create your account for {email}</div>

      <EmailValidationIndicator
        status={emailValidationStatus as never}
        reason={emailInvalidReason ?? undefined}
        onChangeEmail={onChangeEmail}
        className="mb-5"
      />

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-5"
          >
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <p className="text-center text-sm text-red-600">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit(onSubmitName)} className={ui.sectionGap}>
        <div className="space-y-2">
          <label
            htmlFor="firstName"
            className="block text-sm font-semibold text-gray-800"
          >
            {t('multiMethod.firstName')}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-inner">
                <User className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <input
              {...register('firstName')}
              id="firstName"
              type="text"
              placeholder={t('multiMethod.firstNamePlaceholder')}
              autoComplete="given-name"
              autoFocus
              className={
                `w-full ${ui.inputHeight} rounded-2xl border border-gray-200 pl-14 pr-4 text-base ` +
                'hover:border-gray-300 focus:border-emerald-500 focus:outline-hidden focus:ring-4 ' +
                'bg-slate-50 text-gray-900 transition-all duration-200 placeholder:text-gray-400 focus:ring-emerald-500/15'
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="lastName"
            className="block text-sm font-semibold text-gray-800"
          >
            {t('multiMethod.lastName')}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-inner">
                <User className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <input
              {...register('lastName')}
              id="lastName"
              type="text"
              placeholder={t('multiMethod.lastNamePlaceholder')}
              autoComplete="family-name"
              className={
                `w-full ${ui.inputHeight} rounded-2xl border border-gray-200 pl-14 pr-4 text-base ` +
                'hover:border-gray-300 focus:border-emerald-500 focus:outline-hidden focus:ring-4 ' +
                'bg-slate-50 text-gray-900 transition-all duration-200 placeholder:text-gray-400 focus:ring-emerald-500/15'
              }
            />
          </div>
        </div>

        {/* GDPR consent checkboxes */}
        <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="otp-termsOfService">
            <input
              id="otp-termsOfService"
              type="checkbox"
              {...register('termsOfService')}
              className="mt-0.5 h-4 w-4 rounded-sm border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
              aria-invalid={!!errors.termsOfService}
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
              {t('multiMethod.agreeToTerms', 'I agree to the')}{' '}
              <LocaleLink to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                {t('multiMethod.termsOfService', 'Terms of Service')}
              </LocaleLink>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="otp-privacyPolicy">
            <input
              id="otp-privacyPolicy"
              type="checkbox"
              {...register('privacyPolicy')}
              className="mt-0.5 h-4 w-4 rounded-sm border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
              aria-invalid={!!errors.privacyPolicy}
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
              {t('multiMethod.acknowledgePrivacy', 'I acknowledge the')}{' '}
              <LocaleLink to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                {t('multiMethod.privacyPolicy', 'Privacy Policy')}
              </LocaleLink>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="otp-marketingEmail">
            <input
              id="otp-marketingEmail"
              type="checkbox"
              {...register('marketingEmail')}
              className="mt-0.5 h-4 w-4 rounded-sm border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
            />
            <span className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
              {t('multiMethod.marketingConsent', 'Send me updates about campaigns and giving opportunities')}
            </span>
          </label>
        </div>

        <motion.button
          type="submit"
          disabled={
            !isNameValid || !isConsentValid || isSendingOtp || emailValidationStatus === 'invalid'
          }
          whileHover={
            isNameValid && isConsentValid && !isSendingOtp && emailValidationStatus !== 'invalid'
              ? { scale: 1.01 }
              : {}
          }
          whileTap={
            isNameValid && isConsentValid && !isSendingOtp && emailValidationStatus !== 'invalid'
              ? { scale: 0.99 }
              : {}
          }
          className={
            `w-full ${ui.primaryBtnHeight} rounded-2xl text-base font-semibold transition-all duration-200 ` +
            'flex items-center justify-center gap-2 ' +
            (isNameValid && isConsentValid && !isSendingOtp && emailValidationStatus !== 'invalid'
              ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/25 hover:bg-emerald-500'
              : 'cursor-not-allowed bg-gray-100 text-gray-400')
          }
        >
          {isSendingOtp ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('multiMethod.creatingAccount')}
            </>
          ) : (
            <>
              {t('multiMethod.submitName')}
              <ChevronRight className="h-5 w-5" />
            </>
          )}
        </motion.button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onChangeEmail}
          className="text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          {t('multiMethod.useDifferentEmail')}
        </button>
      </div>
    </>
  );
};
